import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { LogOut, RefreshCcw, ExternalLink, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { BUSINESS } from "../lib/constants";
import { STATUS_OPTIONS } from "../lib/quoteStatus";
import { devLog } from "../lib/devLog";
import { Toaster } from "../components/ui/sonner";
import { DashboardFilters } from "../components/admin/DashboardFilters";
import { QuoteTable } from "../components/admin/QuoteTable";
import { QuoteDetailDialog } from "../components/admin/QuoteDetailDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/quotes`, {
        withCredentials: true,
      });
      setQuotes(data);
    } catch (e) {
      devLog("Failed to load quotes:", e);
      toast.error("Couldn't load quotes. Please log in again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return quotes.filter((it) => {
      if (statusFilter !== "all" && (it.status || "new") !== statusFilter)
        return false;
      if (!q) return true;
      return [it.name, it.phone, it.email, it.service, it.description]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [quotes, search, statusFilter]);

  const counts = useMemo(() => {
    const base = { all: quotes.length };
    STATUS_OPTIONS.forEach(
      (s) => (base[s] = quotes.filter((q) => (q.status || "new") === s).length)
    );
    return base;
  }, [quotes]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({ id });
    try {
      const { data } = await axios.get(`${API}/admin/quotes/${id}`, {
        withCredentials: true,
      });
      setDetail(data);
    } catch (e) {
      devLog("Failed to load quote detail:", e);
      toast.error("Failed to load details.");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/quotes/${id}`,
        { status },
        { withCredentials: true }
      );
      setQuotes((arr) => arr.map((q) => (q.id === id ? { ...q, status } : q)));
      if (detail && detail.id === id) setDetail({ ...detail, status });
      toast.success(`Marked as ${status}`);
    } catch (e) {
      devLog("Status update failed:", e);
      toast.error("Update failed.");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/quotes-export.csv`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "text/csv" })
      );
      const a = document.createElement("a");
      a.href = url;
      const d = new Date();
      const ts = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}-${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}`;
      a.download = `bc-quotes-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      devLog("CSV export failed:", e);
      toast.error("Export failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this quote? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/quotes/${id}`, {
        withCredentials: true,
      });
      setQuotes((arr) => arr.filter((q) => q.id !== id));
      if (detail && detail.id === id) setDetail(null);
      toast.success("Deleted");
    } catch (e) {
      devLog("Delete failed:", e);
      toast.error("Delete failed.");
    }
  };

  return (
    <div className="min-h-screen bg-offwhite text-navy" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-navy text-white border-b-2 border-gold">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BUSINESS.logo} alt="B&C" className="h-9 w-9 object-contain" />
            <div>
              <p className="font-display uppercase text-sm leading-none">
                B&amp;C Admin
              </p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/60">
                Quote Inbox
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.18em] text-white/80 hover:text-gold"
            >
              <ExternalLink className="h-3.5 w-3.5" /> View site
            </Link>
            <span className="hidden md:inline text-xs text-white/60 font-body truncate max-w-[200px]">
              {user?.email}
            </span>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 bg-gold text-navy px-3 py-2 font-display uppercase text-xs tracking-wider border-2 border-gold hover:bg-white transition-colors"
            >
              <LogOut className="h-4 w-4" strokeWidth={2.5} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header row */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <span className="label-pill">
              <span className="h-1.5 w-1.5 bg-gold" />
              Dashboard
            </span>
            <h1 className="font-display uppercase text-4xl sm:text-5xl mt-4 leading-none">
              Quote <span className="text-gold">Inbox</span>
            </h1>
            <p className="mt-2 font-body text-navy/70">
              All incoming quote requests — newest first.
            </p>
          </div>

          <div className="flex items-center gap-2 self-start">
            <button
              onClick={exportCsv}
              data-testid="admin-export-csv-btn"
              className="brutal-btn inline-flex items-center gap-2 bg-gold text-navy px-4 py-3 font-display text-xs uppercase tracking-wider border-2 border-navy brutal-btn-gold"
            >
              <Download className="h-4 w-4" strokeWidth={2.5} />
              Export CSV
            </button>
            <button
              onClick={load}
              data-testid="admin-refresh-btn"
              className="brutal-btn inline-flex items-center gap-2 bg-navy text-white px-4 py-3 font-display text-xs uppercase tracking-wider border-2 border-navy"
            >
              <RefreshCcw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                strokeWidth={2.5}
              />
              Refresh
            </button>
          </div>
        </div>

        <DashboardFilters
          statusFilter={statusFilter}
          onFilterChange={setStatusFilter}
          counts={counts}
          search={search}
          onSearchChange={setSearch}
        />

        <QuoteTable
          quotes={filtered}
          loading={loading}
          onView={openDetail}
          onDelete={remove}
          onStatus={setStatus}
        />
      </main>

      <QuoteDetailDialog
        detail={detail}
        loading={detailLoading}
        onClose={() => setDetail(null)}
        onStatus={setStatus}
        onDelete={remove}
      />

      <Toaster position="top-center" />
    </div>
  );
}
